import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { Appointment, Receipt, Staff } from '@salonomy/types';
import { Button, Card, EmptyState, Pill, Stat, TextInput } from '@salonomy/ui';
import { useAppointments, useUpdateAppointment } from '../data/appointments.js';
import { useCapturePayment, useCreatePaymentIntent } from '../data/payments.js';
import { useIssueReceipt } from '../data/receipts.js';
import { useSalon, useStaff } from '../data/salon.js';
import { useServices } from '../data/services.js';
import { DiscountModal } from '../features/till/DiscountModal.js';
import { RedeemPointsModal } from '../features/till/RedeemPointsModal.js';
import { IssueGiftCardModal } from '../features/till/IssueGiftCardModal.js';
import { PackageBalanceModal } from '../features/till/PackageBalanceModal.js';
import { RedeemPackageModal } from '../features/till/RedeemPackageModal.js';
import { ReceiptActions } from '../features/till/ReceiptActions.js';
import { SellMembershipModal } from '../features/till/SellMembershipModal.js';
import { SellPackageModal } from '../features/till/SellPackageModal.js';
import { TipModal, type CapturedTip, type TipStaffOption } from '../features/till/TipModal.js';
import { settleChargePence } from '../features/till/money.js';
import { VoucherBalanceModal } from '../features/till/VoucherBalanceModal.js';
import { VoucherRedeemModal } from '../features/till/VoucherRedeemModal.js';
import { ApiClientError } from '../lib/api.js';
import { money } from '../lib/format.js';
import { useToast } from '../lib/toast.js';
import { clockLabelAt, todayInZone } from '../lib/time.js';

export function TakingsPage() {
  const { data: salon } = useSalon();
  const tz = salon?.timezone ?? 'Europe/London';
  const today = todayInZone(tz);
  const { data: appts = [] } = useAppointments(today, salon?.timezone);
  const { data: staff } = useStaff();
  const { data: services } = useServices();
  const update = useUpdateAppointment(today);
  const toast = useToast();
  const navigate = useNavigate();

  const initialAppt = new URLSearchParams(window.location.search).get('appt');
  const [selectedId, setSelectedId] = useState<string | null>(initialAppt);
  // Till-wide actions (not tied to a selected booking): sell a gift card or package, look up a balance.
  const [issuing, setIssuing] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [sellingPackage, setSellingPackage] = useState(false);
  const [sellingMembership, setSellingMembership] = useState(false);
  const [lookingUpPackage, setLookingUpPackage] = useState(false);

  const open = appts.filter(
    (a) => a.status !== 'completed' && a.status !== 'cancelled' && a.status !== 'no_show',
  );
  const done = appts.filter((a) => a.status === 'completed');
  const total = done.reduce((s, a) => s + a.priceQuoted, 0);
  const selected = useMemo(
    () => appts.find((a) => a.id === selectedId) ?? open[0] ?? null,
    [appts, selectedId, open],
  );

  return (
    <>
      <div className="topbar">
        <div className="topbar__left">
          <div className="topbar__title">Takings</div>
          <span className="muted">{today}</span>
        </div>
        <div className="topbar__right">
          <Button variant="outline" size="sm" onClick={() => setLookingUp(true)}>
            Voucher balance
          </Button>
          <Button variant="outline" size="sm" onClick={() => setIssuing(true)}>
            Issue gift card
          </Button>
          <Button variant="outline" size="sm" onClick={() => setLookingUpPackage(true)}>
            Package balance
          </Button>
          <Button variant="outline" size="sm" onClick={() => setSellingPackage(true)}>
            Sell package
          </Button>
          <Button variant="outline" size="sm" onClick={() => setSellingMembership(true)}>
            Start membership
          </Button>
          <Stat label="Today" value={money(total)} />
        </div>
      </div>

      <div className="screen-scroll">
        <div className="screen-pad till">
          <div className="till__queue">
            <div className="section-title">Awaiting checkout</div>
            {open.length === 0 ? (
              <EmptyState title="All squared up" description="No open checkouts right now." />
            ) : (
              open.map((a) => (
                <button
                  key={a.id}
                  className={`till-row${selected?.id === a.id ? ' till-row--active' : ''}`}
                  onClick={() => setSelectedId(a.id)}
                >
                  <div style={{ minWidth: 0 }}>
                    <div className="client-row__name">{a.clientDisplayName || 'Walk-in'}</div>
                    <div className="client-row__sub">
                      {clockLabelAt(a.startAt, tz)} ·{' '}
                      {services?.find((s) => s.id === a.serviceId)?.name ?? 'Service'}
                    </div>
                  </div>
                  <span className="mono">{money(a.priceQuoted)}</span>
                </button>
              ))
            )}

            {done.length ? (
              <>
                <div className="section-title" style={{ marginTop: 20 }}>
                  Completed today
                </div>
                {done.map((a) => (
                  <div key={a.id} className="till-row till-row--done">
                    <div className="client-row__name">{a.clientDisplayName || 'Walk-in'}</div>
                    <span className="mono">{money(a.priceQuoted)}</span>
                  </div>
                ))}
              </>
            ) : null}
          </div>

          <div className="till__checkout">
            {selected ? (
              <Checkout
                key={selected.id}
                appt={selected}
                serviceName={services?.find((s) => s.id === selected.serviceId)?.name ?? 'Service'}
                renterName={staff?.find((s) => s.id === selected.renterId)?.displayName ?? null}
                staff={staff ?? []}
                onCompleted={() => toast.success('Payment taken — rebook released')}
                markCompleted={(id) => update.mutateAsync({ id, body: { status: 'completed' } })}
                onRebook={() => {
                  setSelectedId(null);
                  void navigate({ to: '/' });
                }}
              />
            ) : (
              <Card>
                <div className="muted" style={{ padding: 12 }}>
                  Select a booking to check out.
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>

      {issuing ? (
        <IssueGiftCardModal
          clientId={selected?.clientId ?? null}
          onIssued={() => {
            /* receipt line is surfaced by the modal's toast; the till stays on the basket */
          }}
          onClose={() => setIssuing(false)}
        />
      ) : null}
      {lookingUp ? <VoucherBalanceModal onClose={() => setLookingUp(false)} /> : null}
      {sellingPackage ? (
        <SellPackageModal
          clientId={selected?.clientId ?? null}
          clientName={selected?.clientDisplayName ?? null}
          onSold={() => {
            /* the sale receipt line is surfaced by the modal's toast; the till stays on the basket */
          }}
          onClose={() => setSellingPackage(false)}
        />
      ) : null}
      {sellingMembership ? (
        <SellMembershipModal
          clientId={selected?.clientId ?? null}
          clientName={selected?.clientDisplayName ?? null}
          onSold={() => {
            /* a membership takes no money at the till (it's a connected-account subscription); the
               modal's toast confirms it started, and the till stays on the basket */
          }}
          onClose={() => setSellingMembership(false)}
        />
      ) : null}
      {lookingUpPackage ? (
        <PackageBalanceModal
          clientId={selected?.clientId ?? null}
          clientName={selected?.clientDisplayName ?? null}
          onClose={() => setLookingUpPackage(false)}
        />
      ) : null}
    </>
  );
}

/** A voucher tender applied at checkout — the server-confirmed amount the liability moved by. */
interface VoucherTender {
  id: string;
  amountPence: number;
}
/** A discount applied at checkout — the server-COMPUTED reduction (never recomputed locally). */
interface AppliedDiscount {
  id: string;
  discountPence: number;
}

function Checkout({
  appt,
  serviceName,
  renterName,
  staff,
  onCompleted,
  markCompleted,
  onRebook,
}: {
  appt: Appointment;
  serviceName: string;
  renterName: string | null;
  /** Salon staff — the tip modal's serving-staff options (primary + any split staff). */
  staff: Staff[];
  /** Fired once the sale has settled end-to-end (payment captured, receipt issued, appt completed). */
  onCompleted: () => void;
  /** Marks the appointment completed on the server — called ONLY after payment + receipt succeed. */
  markCompleted: (id: string) => Promise<unknown>;
  onRebook: () => void;
}) {
  const toast = useToast();
  const createIntent = useCreatePaymentIntent();
  const capture = useCapturePayment();
  const issueReceipt = useIssueReceipt();
  const total = appt.priceQuoted;

  // The issued receipt for this sale. Once set, the basket is settled and ReceiptActions takes over;
  // it survives the appointment status flipping to `completed` because we hold it in local state.
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const settled = receipt !== null || appt.status === 'completed';

  // WS9 — discounts (server-computed reductions) and voucher tenders applied against this basket. We
  // store the amounts the SERVER returned and only SUM them for the running "remaining" line; no money
  // math is invented here. Keyed on the appointment via the parent's `key`, so they reset per basket.
  const [discounts, setDiscounts] = useState<AppliedDiscount[]>([]);
  const [vouchers, setVouchers] = useState<VoucherTender[]>([]);
  // WS-13 — prepaid packages redeemed at this checkout. A redemption is a TENDER (the server-confirmed
  // value the package liability moved by), exactly like a voucher tender; we store the server figure and
  // only SUM it into "remaining" — no money math is invented here.
  const [packageTenders, setPackageTenders] = useState<VoucherTender[]>([]);
  // WS-13 — loyalty points redeemed at this checkout. A redeem is a server-side `discount` line
  // (reasonCode 'loyalty'); we store the SERVER-computed reduction and only SUM it into "remaining" —
  // no points→£ math is invented here. Kept separate from manual discounts so the line label is honest.
  const [pointsRedemptions, setPointsRedemptions] = useState<AppliedDiscount[]>([]);
  // WS14 — tips captured at the till. A tip is the staff member's gratuity (100% theirs, VAT-free, NEVER
  // commissionable). Captured locally as [{staffId, amountPence}] — NO server call here; the tips are
  // materialised into VAT-0 receipt lines when issue-receipt runs. They ADD to the charged amount (unlike a
  // discount/voucher, which reduce it): the till charges service-remaining + tips, then passes `tips` on.
  const [tips, setTips] = useState<CapturedTip[]>([]);
  const [showRedeem, setShowRedeem] = useState(false);
  const [showDiscount, setShowDiscount] = useState(false);
  const [showPoints, setShowPoints] = useState(false);
  const [showPackage, setShowPackage] = useState(false);
  const [showTip, setShowTip] = useState(false);

  const dueAfterDeposit = Math.max(0, total - appt.depositPaid);
  const discountTotal = discounts.reduce((s, d) => s + d.discountPence, 0);
  const pointsTotal = pointsRedemptions.reduce((s, d) => s + d.discountPence, 0);
  const voucherTotal = vouchers.reduce((s, v) => s + v.amountPence, 0);
  const packageTotal = packageTenders.reduce((s, p) => s + p.amountPence, 0);
  // The outstanding amount still to take on card/cash after deposit, discounts, points, vouchers and
  // package redemptions (all server figures — never recomputed, only summed for the running "remaining").
  const remaining = Math.max(
    0,
    dueAfterDeposit - discountTotal - pointsTotal - voucherTotal - packageTotal,
  );

  // WS14 — the captured tips' total (pence). Tips ADD to what's charged; service-remaining stays the
  // server-figured outstanding. This is the customer's chosen gratuity, never a recognised-revenue figure.
  const tipTotal = tips.reduce((s, t) => s + t.amountPence, 0);
  // The serving-staff a tip can be attributed to: the appointment's primary staff plus any staff who
  // already carry a service split for this basket. (Today the till books a single staff per appointment;
  // this still future-proofs multi-staff splits.) De-duped, named from the staff directory.
  const tipStaffOptions: TipStaffOption[] = useMemo(() => {
    const ids = new Set<string>([appt.staffId]);
    return [...ids]
      .map((id) => {
        const member = staff.find((s) => s.id === id);
        return member ? { id: member.id, name: member.displayName } : null;
      })
      .filter((o): o is TipStaffOption => o !== null);
  }, [appt.staffId, staff]);

  const [card, setCard] = useState(remaining);
  // The card field is the split's editable leg; cash fills the rest. Clamp card to the live remaining so
  // a discount/redemption applied after a manual edit can't leave card > remaining.
  const cardApplied = Math.min(card, remaining);
  const cash = Math.max(0, remaining - cardApplied);

  // Any mutation in flight gates the settle button so a double-tap can't fire a second take.
  const taking = createIntent.isPending || capture.isPending || issueReceipt.isPending;

  // Settle: take the money, issue the receipt, THEN mark the appointment completed — in that order so a
  // failed capture never marks the booking done. A voucher/gift-card tender may fully cover the balance
  // (remaining === 0) → a £0 card intent that still produces a receipt. Idempotent on a key minted ONCE
  // for this take: a re-delivered create/capture/issue replays the original and moves no extra money.
  async function settle() {
    if (settled || taking) return;
    const idempotencyKey = crypto.randomUUID();
    // WS14 — the till charges the SERVICE remaining PLUS any captured tips together on one intent. The
    // tip rides issue-receipt (not the intent body); the server carves the service portion back out
    // (= payment.amountPence − Σtips) and reconciles shown==charged. With a single serving staff we omit
    // splits and let the server default the single service line to (payment.amountPence − Σtips).
    const chargeAmount = settleChargePence(remaining, tips);
    // Terminal-captured in person for the card leg; with no card due (fully covered) it's a £0 record.
    const method = cardApplied > 0 || tipTotal > 0 ? 'terminal' : 'online';
    try {
      const intent = await createIntent.mutateAsync({
        appointmentId: appt.id,
        amountPence: chargeAmount,
        kind: 'balance',
        method,
        idempotencyKey,
      });
      // Online intents auto-capture; a Terminal leg needs an explicit manual capture before a receipt.
      let payment = intent.payment;
      if (method === 'terminal' && payment.status !== 'succeeded') {
        payment = await capture.mutateAsync({ id: payment.id, idempotencyKey });
      }
      const issued = await issueReceipt.mutateAsync({
        paymentId: payment.id,
        appointmentId: appt.id,
        // Lines omitted: the server derives the service line from the payment + splits (and subtracts the
        // tip total). `tips` materialise one VAT-0 receipt line each. Don't auto-email on issue — the
        // staff send it from ReceiptActions if the client wants it.
        tips: tips.length ? tips : undefined,
        sendEmail: false,
      });
      // Money's taken and the receipt is gapless — only now flip the booking to completed.
      await markCompleted(appt.id);
      setReceipt(issued);
      onCompleted();
    } catch (err) {
      // A failed capture/issue leaves the appointment OPEN — nothing was marked completed.
      toast.error(
        err instanceof ApiClientError
          ? err.message
          : 'Could not take payment — nothing was charged',
      );
    }
  }

  return (
    <Card>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontFamily: 'var(--slm-font-display)', fontSize: 22 }}>
              {appt.clientDisplayName || 'Walk-in'}
            </div>
            <div className="muted">{serviceName}</div>
          </div>
          {renterName ? <Pill tone="neutral">{renterName}’s book</Pill> : null}
        </div>

        <div className="till__line">
          <span>{serviceName}</span>
          <span className="mono">{money(total)}</span>
        </div>
        {appt.depositPaid ? (
          <div className="till__line muted">
            <span>Deposit paid</span>
            <span className="mono">− {money(appt.depositPaid)}</span>
          </div>
        ) : null}
        {discounts.map((d) => (
          <div key={d.id} className="till__line muted">
            <span>Discount</span>
            <span className="mono">− {money(d.discountPence)}</span>
          </div>
        ))}
        {pointsRedemptions.map((d) => (
          <div key={d.id} className="till__line muted">
            <span>Points redeemed</span>
            <span className="mono">− {money(d.discountPence)}</span>
          </div>
        ))}
        {vouchers.map((v) => (
          <div key={v.id} className="till__line muted">
            <span>Voucher tendered</span>
            <span className="mono">− {money(v.amountPence)}</span>
          </div>
        ))}
        {packageTenders.map((p) => (
          <div key={p.id} className="till__line muted">
            <span>Package redeemed</span>
            <span className="mono">− {money(p.amountPence)}</span>
          </div>
        ))}
        {tips.map((t, i) => {
          const name = staff.find((s) => s.id === t.staffId)?.displayName ?? 'Staff';
          return (
            <div key={`tip-${t.staffId}-${i}`} className="till__line muted">
              <span>
                Tip · {name}
                {!settled ? (
                  <button
                    type="button"
                    aria-label={`Remove tip for ${name}`}
                    style={{
                      marginLeft: 8,
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      color: 'inherit',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      font: 'inherit',
                    }}
                    onClick={() => setTips((cur) => cur.filter((_, j) => j !== i))}
                  >
                    Remove
                  </button>
                ) : null}
              </span>
              <span className="mono">+ {money(t.amountPence)}</span>
            </div>
          );
        })}
        <div className="till__line till__line--total">
          <span>{remaining + tipTotal > 0 ? 'Charge now' : 'Settled'}</span>
          <span className="mono">{money(remaining + tipTotal)}</span>
        </div>

        {!settled ? (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button variant="outline" size="sm" onClick={() => setShowRedeem(true)}>
              Redeem voucher
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowPackage(true)}>
              Redeem package
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowPoints(true)}>
              Redeem points
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowDiscount(true)}>
              Add discount
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={tipStaffOptions.length === 0}
              onClick={() => setShowTip(true)}
            >
              Add tip
            </Button>
          </div>
        ) : null}

        <div>
          <div className="slm-stat__label" style={{ marginBottom: 8 }}>
            Split payment
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label className="till__split">
              <span>Card</span>
              <TextInput
                type="number"
                value={(cardApplied / 100).toFixed(2)}
                onChange={(e) => setCard(Math.round(Number(e.target.value) * 100) || 0)}
              />
            </label>
            <label className="till__split">
              <span>Cash</span>
              <TextInput type="number" readOnly value={(cash / 100).toFixed(2)} />
            </label>
          </div>
        </div>

        {settled ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Pill tone="success">Paid · receipt ready</Pill>
            {receipt ? (
              <ReceiptActions receipt={receipt} />
            ) : (
              <div className="muted" style={{ fontSize: 12 }}>
                This booking is already settled. Look up its receipt in takings history to reprint
                or resend.
              </div>
            )}
            <Button onClick={onRebook}>Rebook →</Button>
          </div>
        ) : (
          <>
            <Button size="lg" onClick={() => void settle()} disabled={taking}>
              {taking
                ? 'Taking…'
                : remaining + tipTotal > 0
                  ? `Take ${money(remaining + tipTotal)} & release rebook`
                  : 'Settle & release rebook'}
            </Button>
            <div className="muted" style={{ fontSize: 11.5, lineHeight: 1.5 }}>
              Settle takes the card/cash balance, issues an itemised VAT receipt and only then marks
              the booking completed — a failed capture leaves it open and charges nothing. Voucher
              tenders and discounts are recorded server-side as they&rsquo;re applied; a tender that
              fully covers the balance still issues a £0 receipt. Every figure shown is the till
              server&rsquo;s, not recomputed here.
            </div>
          </>
        )}
      </div>

      {showRedeem ? (
        <VoucherRedeemModal
          appointmentId={appt.id}
          clientId={appt.clientId}
          serviceName={serviceName}
          dueNowPence={remaining}
          onRedeemed={(amountPence) =>
            setVouchers((v) => [...v, { id: crypto.randomUUID(), amountPence }])
          }
          onClose={() => setShowRedeem(false)}
        />
      ) : null}
      {showDiscount ? (
        <DiscountModal
          appointmentId={appt.id}
          clientId={appt.clientId}
          basePence={remaining}
          onApplied={(discountPence) =>
            setDiscounts((d) => [...d, { id: crypto.randomUUID(), discountPence }])
          }
          onClose={() => setShowDiscount(false)}
        />
      ) : null}
      {showPackage ? (
        <RedeemPackageModal
          appointmentId={appt.id}
          clientId={appt.clientId}
          serviceName={serviceName}
          dueNowPence={remaining}
          onRedeemed={(amountPence) =>
            setPackageTenders((p) => [...p, { id: crypto.randomUUID(), amountPence }])
          }
          onClose={() => setShowPackage(false)}
        />
      ) : null}
      {showPoints ? (
        <RedeemPointsModal
          clientId={appt.clientId}
          appointmentId={appt.id}
          onRedeemed={(discountPence) =>
            setPointsRedemptions((d) => [...d, { id: crypto.randomUUID(), discountPence }])
          }
          onClose={() => setShowPoints(false)}
        />
      ) : null}
      {showTip ? (
        <TipModal
          basePence={remaining}
          staff={tipStaffOptions}
          defaultStaffId={appt.staffId}
          onCaptured={(captured) => setTips((cur) => [...cur, ...captured])}
          onClose={() => setShowTip(false)}
        />
      ) : null}
    </Card>
  );
}
